import React from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { mergeStyleSets } from '@fluentui/react';
import { Plus, Minus, ArrowsOut, CornersOut } from '@phosphor-icons/react';
import { boroondaraPalette } from '../../theme/boroondaraTheme';
import { GeoJSONFeatureCollection } from './types';

const p = boroondaraPalette;

interface MapToolbarProps {
  featureData: Record<string, GeoJSONFeatureCollection>;
  activeLayers: Set<string>;
}

const styles = mergeStyleSets({
  toolbar: {
    position: 'absolute' as any,
    top: 12,
    right: 12,
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  } as any,
  btn: {
    width: 34,
    height: 34,
    borderRadius: 4,
    border: `1px solid ${p.border}`,
    background: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
    selectors: { ':hover': { background: p.bg2, borderColor: p.primary } },
  } as any,
});

function ToolbarInner({ featureData, activeLayers }: MapToolbarProps) {
  const map = useMap();

  const handleFullscreen = () => {
    const el = map.getContainer().closest('.mapSection') as HTMLElement | null;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  const handleZoomFit = () => {
    const allCoords: L.LatLng[] = [];
    activeLayers.forEach((id) => {
      const fc = featureData[id];
      if (!fc?.features) return;
      fc.features.forEach((f) => {
        if (!f.geometry) return;
        if (f.geometry.type === 'Point') {
          const [lng, lat] = f.geometry.coordinates;
          allCoords.push(L.latLng(lat, lng));
        } else if (f.geometry.type === 'Polygon') {
          f.geometry.coordinates[0].forEach(([lng, lat]: number[]) => {
            allCoords.push(L.latLng(lat, lng));
          });
        } else if (f.geometry.type === 'MultiPolygon') {
          f.geometry.coordinates.forEach((poly: number[][][]) => {
            poly[0].forEach(([lng, lat]: number[]) => {
              allCoords.push(L.latLng(lat, lng));
            });
          });
        }
      });
    });
    if (allCoords.length > 0) {
      map.fitBounds(L.latLngBounds(allCoords), { padding: [30, 30] });
    }
  };

  const handleZoomIn = () => map.zoomIn();
  const handleZoomOut = () => map.zoomOut();

  return (
    <div className={styles.toolbar}>
      <button className={styles.btn} onClick={handleZoomIn} title="Zoom in">
        <Plus size={14} weight="bold" style={{ color: p.text2 }} />
      </button>
      <button className={styles.btn} onClick={handleZoomOut} title="Zoom out">
        <Minus size={14} weight="bold" style={{ color: p.text2 }} />
      </button>
      <button className={styles.btn} onClick={handleZoomFit} title="Zoom to fit all layers">
        <ArrowsOut size={14} weight="bold" style={{ color: p.text2 }} />
      </button>
      <button className={styles.btn} onClick={handleFullscreen} title="Toggle fullscreen">
        <CornersOut size={14} weight="bold" style={{ color: p.text2 }} />
      </button>
    </div>
  );
}

export default function MapToolbar(props: MapToolbarProps) {
  return <ToolbarInner {...props} />;
}
